import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, makeStyles,
} from '@material-ui/core';
import ProcedureNumberCell from './ProcedureNumberCell';
import { MATERIALS_DATA, mergeTabData, fmtDateTime } from './manufacturingTabData';
import Pagination from '../../logisticsDashboard/components/Pagination';

const useStyles = makeStyles(() => ({
  wrapper: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  tableScroll: {
    width: '100%',
    overflowX: 'auto',
    maxHeight: 440,
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    tableLayout: 'auto',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.85rem',
      color: '#333',
      padding: '8px 4px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      overflow: 'hidden',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.85rem',
      padding: '8px 4px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'hidden',
    },
  },
}));

const COLUMNS = [
  'Unit Name',
  'Phase / Step Number',
  'Unit Procedure',
  'Operation',
  'Phase',
  'Material Description',
  'CMO Material ID',
  'CMO Batch Number',
  'Target Quantity',
  'Actual Quantity',
  'UoM',
  'Quantity Variance (%)',
];

// Each column takes the API value where present, otherwise falls back to the
// "Material and Consumption" sheet of the batch's Manufacturing Collaboration
// workbook (see mergeTabData). Any other batch's fallback carries its own batch number.
const MaterialsConsumptionTab = ({ data, loading, batchNumber, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const rows = mergeTabData(data, MATERIALS_DATA, batchNumber);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.wrapper} elevation={0}>
      <div className={classes.tableScroll}>
        <Table className={classes.table} stickyHeader size="small">
          <TableHead>
            <TableRow className={classes.headerRow}>
              {COLUMNS.map(col => (
                <TableCell key={col}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={COLUMNS.length} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, idx) => (
              <TableRow key={idx} className={classes.bodyRow}>
                <TableCell style={{ maxWidth: 200 }}>{row.unitName}</TableCell>
                <TableCell><ProcedureNumberCell value={row.procedureNumber} batchNumber={batchNumber} /></TableCell>
                <TableCell style={{ maxWidth: 180 }}>{row.unitProcedure}</TableCell>
                <TableCell style={{ maxWidth: 170 }}>{row.operation}</TableCell>
                <TableCell style={{ maxWidth: 280 }}>{row.phase}</TableCell>
                <TableCell>{row.materialDescription}</TableCell>
                <TableCell>{row.cmoMaterialCode}</TableCell>
                <TableCell>{row.cmoBatchNumber}</TableCell>
                <TableCell>{row.targetQuantity}</TableCell>
                <TableCell>{row.actualQuantity}</TableCell>
                <TableCell>{row.uom}</TableCell>
                <TableCell>{row.quantityVariance || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Paper>
  );
};

export default MaterialsConsumptionTab;
